import { useCallback, useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { T } from "@repo/transifex";
import { ModalPrintingError } from "~/components/kiosk/ui/modals/checkout/printing/printing-error";
import { ModalPrintingLoading } from "~/components/kiosk/ui/modals/checkout/printing/printing-loading";
import { ModalPrintingSuccess } from "~/components/kiosk/ui/modals/checkout/printing/printing-success";
import { ModalButton } from "~/components/kiosk/ui/modals/shared/modal-button";
import {
  usePrintOrderReceipt,
  useReceiptMutationStatus
} from "~/hooks/queries/orders";
import { useAppDispatch, usePrinterStatus } from "~/providers/app";
import { logger } from "~/services/logger";
import { useKioskSession } from "~/stores/kiosk-session/hooks/use-kiosk-session";
import { useKioskSessionReset } from "~/stores/kiosk-session/hooks/use-kiosk-session-reset";

import { useKioskStore } from "../__app.kiosk/route";

type PrintPageState = {
  receiptList?: string[];
} | null;

export const KioskPrintPage = () => {
  const user = useKioskStore((state) => state.user);
  const orderId = useKioskSession((session) => session?.orderId);
  const location = useLocation();
  const state = location.state as PrintPageState;

  const dispatch = useAppDispatch();

  const { mutate: createReceipt } = usePrintOrderReceipt();

  const latestCreateReceiptStatus = useReceiptMutationStatus(orderId);

  const printerStatus = usePrinterStatus();

  const resetSession = useKioskSessionReset();

  const hasPrinted = useRef(false);

  const hasError =
    printerStatus === "error" || latestCreateReceiptStatus === "error";

  const print = useCallback(() => {
    if (!orderId) return;
    dispatch({ type: "PRINTER_PRINT_RECEIPT_RESET" });
    createReceipt(
      {
        orderId,
        userId: user?.id ?? null,
        receiptList: state?.receiptList
      },
      {
        onError: (error) => {
          logger.error(error, "[KIOSK_PRINT_ERROR]", { orderId });
        }
      }
    );
  }, [createReceipt, dispatch, orderId, state?.receiptList, user?.id]);

  useEffect(() => {
    if (orderId && !hasPrinted.current && latestCreateReceiptStatus === null) {
      hasPrinted.current = true;
      print();
    }
  }, [latestCreateReceiptStatus, orderId, print]);

  if (!orderId) {
    return <Navigate to=".." />;
  }

  if (hasError) {
    return <ModalPrintingError onRetry={print} onClose={resetSession} />;
  }

  if (printerStatus === "success") {
    return <ModalPrintingSuccess onClose={resetSession} />;
  }

  if (printerStatus === "loading") {
    return <ModalPrintingLoading />;
  }

  return (
    <ModalPrintingLoading>
      <ModalButton onClick={resetSession}>
        <T _str="Cancel" />
      </ModalButton>
    </ModalPrintingLoading>
  );
};
